import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import type { Api } from '../adapters/Api';
import { useI18n } from '../i18n';
import { OperationModal } from './OperationModal';
import { botName } from './Fleet';

// Aviso "te protegimos de un ataque" (Auto Shield). El cliente emite
// status_code='auto_shield_protected' + status_params.at (sello del ataque) al
// saltar el escudo; aquí lo detectamos sondeando la flota (misma query que
// Fleet, react-query deduplica) y mostramos el modal UNA vez por evento,
// deduplicando por ese `at` en localStorage (por si la fila sigue en 'shielded'
// durante muchos refetch, o el usuario abre la web después del ataque).

const SHIELD_CODE = 'auto_shield_protected';
const SEEN_KEY = 'white-bot-dl:shieldSeen';

function loadSeen(): Record<string, string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveSeen(seen: Record<string, string>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* localStorage lleno o bloqueado: el dedupe en memoria (seenRef) basta esta sesión */
  }
}

export function ShieldAlarm({ api }: { api: Api }) {
  const { t, language } = useI18n();
  const botsQuery = useQuery({
    queryKey: ['botFleet'],
    queryFn: () => api.listBots(),
    refetchInterval: 3000
  });
  const bots = botsQuery.data;
  // Init perezosa: loadSeen() (localStorage + JSON.parse) solo la 1ª vez, no en
  // cada re-render (AppShell re-renderiza cada ~2-3s por las queries).
  const seenRef = useRef<Record<string, string> | null>(null);
  if (seenRef.current === null) seenRef.current = loadSeen();
  const [alarm, setAlarm] = useState<{ name: string; at: string } | null>(null);

  useEffect(() => {
    if (alarm || !bots) return; // ya hay un modal abierto o aún no hay datos
    const seen = seenRef.current ?? {};
    for (const bot of bots) {
      if (bot.statusCode !== SHIELD_CODE) continue;
      const at = bot.statusParams?.at;
      if (typeof at !== 'string' || !at) continue; // sin sello fiable no disparamos
      if (seen[bot.instanceId] === at) continue; // ya avisado de este ataque
      const next = { ...seen, [bot.instanceId]: at };
      seenRef.current = next;
      saveSeen(next);
      setAlarm({ name: botName(bot, t), at });
      return; // uno cada vez; el siguiente evento fresco sale al cerrar este
    }
  }, [bots, t, alarm]);

  if (!alarm) return null;

  let when = '';
  try {
    const d = new Date(alarm.at);
    if (!Number.isNaN(d.getTime())) when = d.toLocaleString(language);
  } catch {
    when = '';
  }

  return (
    <OperationModal
      active={false}
      tone="warning"
      className="operation-shield"
      icon={<ShieldCheck size={44} strokeWidth={2.2} aria-hidden="true" />}
      titleId="shield-alarm-title"
      eyebrow={t('shieldAlarm.eyebrow')}
      title={t('shieldAlarm.title')}
      body={t('shieldAlarm.body', { name: alarm.name })}
      statusItems={when ? [{ label: t('shieldAlarm.time'), value: when }] : []}
      actions={[{ label: t('shieldAlarm.close'), onClick: () => setAlarm(null), primary: true }]}
    />
  );
}
